import React, { useState, useRef, useEffect } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  isSameMonth,
  isToday,
  startOfDay,
  endOfDay,
  subYears,
  getYear,
  getMonth,
  setYear,
  setMonth,
  isValid,
  parse,
} from "date-fns";

export default function DobPicker({
  title,
  placeholder = "DD/MM/YYYY",
  inputValue,
  onChange,
  name,
  id,
  isValid: isInputValid = true,
  errorText,
  isDisabled = false,
  isReadOnly = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(subYears(new Date(), 20)));
  const [typedValue, setTypedValue] = useState("");
  const dropdownRef = useRef(null);

  const today = startOfDay(new Date());
  const maxSelectableDate = endOfDay(subYears(today, 20));
  const minSelectableDate = startOfDay(subYears(today, 100)); // 100 years back

  // Years for dropdown
  const years = [];
  const startYear = getYear(maxSelectableDate);
  for (let y = startYear; y >= startYear - 80; y--) {
    years.push(y);
  }
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  // Sync internal typed value with external value
  useEffect(() => {
    if (inputValue && typeof inputValue === "string" && inputValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const parsed = new Date(inputValue);
      if (isValid(parsed)) {
        setTypedValue(format(parsed, "dd/MM/yyyy"));
        setCurrentMonth(startOfMonth(parsed));
      } else {
        setTypedValue(inputValue);
      }
    } else if (inputValue) {
      setTypedValue(inputValue);
    } else {
      setTypedValue("");
    }
  }, [inputValue]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleDateClick = (day) => {
    if (day > maxSelectableDate || day < minSelectableDate) return;
    const dateStr = format(day, "yyyy-MM-dd");
    onChange({ target: { name, value: dateStr } });
    setIsOpen(false);
  };

  const handleInputChange = (e) => {
    let val = e.target.value.replace(/[^0-9/]/g, "");

    // Auto-formatting (add slashes)
    if (val.length === 2 && typedValue.length < 2) val += "/";
    if (val.length === 5 && typedValue.length < 5) val += "/";
    if (val.length > 10) val = val.substring(0, 10);

    setTypedValue(val);

    if (val.length === 10) {
      const parsed = parse(val, "dd/MM/yyyy", new Date());
      if (isValid(parsed) && parsed >= minSelectableDate && parsed <= maxSelectableDate) {
        onChange({ target: { name, value: format(parsed, "yyyy-MM-dd") } });
        setCurrentMonth(startOfMonth(parsed));
      } else {
        onChange({ target: { name, value: val } }); // Send invalid to trigger parent validation
      }
    } else {
      onChange({ target: { name, value: val } });
    }
  };

  const renderCalendar = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = [];
    let currentDay = startDate;
    while (currentDay <= endDate) {
      days.push(currentDay);
      currentDay = addDays(currentDay, 1);
    }

    const selectedDate = (inputValue && inputValue.match(/^\d{4}-\d{2}-\d{2}$/)) ? new Date(inputValue) : null;
    const isSelectedValid = selectedDate && isValid(selectedDate);

    return (
      <div style={{ padding: "16px", background: "white", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.15)", width: "320px", position: "absolute", zIndex: 100, top: "100%", left: 0, marginTop: "8px" }}>
        {/* Header navigation */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <div
            onClick={() => setCurrentMonth(addMonths(currentMonth, -1))}
            style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "32px", height: "32px", cursor: "pointer", borderRadius: "8px", transition: "background 0.2s" }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "#f0f0f0"}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <select
              value={getMonth(currentMonth)}
              onChange={(e) => {
                const newMonth = parseInt(e.target.value);
                const nextDate = setMonth(currentMonth, newMonth);
                if (nextDate <= startOfMonth(maxSelectableDate)) {
                  setCurrentMonth(nextDate);
                }
              }}
              style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #E0E0E0", fontSize: "14px", outline: "none", background: "white", cursor: "pointer", fontFamily: "'Gilroy', -apple-system, Roboto, Helvetica, sans-serif", fontWeight: 500, color: "#282828" }}
            >
              {months.map((m, i) => {
                const monthDate = setMonth(currentMonth, i);
                const isMonthDisabled = monthDate > startOfMonth(maxSelectableDate);
                return <option key={m} value={i} disabled={isMonthDisabled}>{m}</option>;
              })}
            </select>
            <select
              value={getYear(currentMonth)}
              onChange={(e) => {
                const newYear = parseInt(e.target.value);
                let nextDate = setYear(currentMonth, newYear);
                if (nextDate > startOfMonth(maxSelectableDate)) {
                  nextDate = startOfMonth(maxSelectableDate);
                }
                setCurrentMonth(nextDate);
              }}
              style={{ padding: "6px 8px", borderRadius: "6px", border: "1px solid #E0E0E0", fontSize: "14px", outline: "none", background: "white", cursor: "pointer", fontFamily: "'Gilroy', -apple-system, Roboto, Helvetica, sans-serif", fontWeight: 500, color: "#282828" }}
            >
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <div
            onClick={() => {
              const nextMonth = addMonths(currentMonth, 1);
              if (nextMonth <= startOfMonth(maxSelectableDate)) {
                setCurrentMonth(nextMonth);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              cursor: addMonths(currentMonth, 1) > startOfMonth(maxSelectableDate) ? "not-allowed" : "pointer",
              borderRadius: "8px",
              transition: "background 0.2s",
              opacity: addMonths(currentMonth, 1) > startOfMonth(maxSelectableDate) ? 0.3 : 1
            }}
            onMouseEnter={(e) => { if (addMonths(currentMonth, 1) <= startOfMonth(maxSelectableDate)) e.currentTarget.style.backgroundColor = "#f0f0f0"; }}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 18L15 12L9 6" stroke={addMonths(currentMonth, 1) > startOfMonth(maxSelectableDate) ? "#ccc" : "#666"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
        </div>

        {/* Days Header */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px", marginBottom: "8px" }}>
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div key={day} style={{ textAlign: "center", fontSize: "12px", color: "#666", fontWeight: 500 }}>{day}</div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "4px" }}>
          {days.map((day, idx) => {
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isTodayDate = isToday(day);
            const isSelected = isSelectedValid && format(day, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
            const isDisabled = day < minSelectableDate || day > maxSelectableDate || !isCurrentMonth;

            return (
              <div
                key={idx}
                onClick={() => !isDisabled && handleDateClick(day)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "36px",
                  borderRadius: "8px",
                  background: isSelected ? "#079F9F" : isTodayDate ? "#f0f0f0" : "transparent",
                  color: isDisabled ? "#ccc" : isSelected ? "white" : "#333",
                  fontWeight: isSelected || isTodayDate ? 600 : 400,
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontFamily: "'Gilroy', -apple-system, Roboto, Helvetica, sans-serif",
                  transition: "background 0.2s"
                }}
                onMouseEnter={(e) => { if (!isDisabled && !isSelected) e.currentTarget.style.backgroundColor = "#f7f7f7"; }}
                onMouseLeave={(e) => { if (!isDisabled && !isSelected) e.currentTarget.style.backgroundColor = isTodayDate ? "#f0f0f0" : "transparent"; }}
              >
                {format(day, "d")}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "12px 16px", paddingRight: "48px",
    fontFamily: "'Gilroy', -apple-system, Roboto, Helvetica, sans-serif",
    fontSize: 14, fontWeight: 500, color: "#282828",
    border: isInputValid ? "1px solid #E0E0E0" : "1px solid #ef4444",
    borderRadius: 8, outline: "none", transition: "border-color 0.15s",
    backgroundColor: isDisabled || isReadOnly ? "white" : "white",
    display: "block",
    /* Prevent Chrome autofill background color */
    WebkitTextFillColor: "#282828",
    transition: "background-color 5000s ease-in-out 0s",
  };

  return (
    <div style={{ width: "100%" }} ref={dropdownRef}>
      <div style={{ marginBottom: 4, position: "relative" }}>
        {title && (
          <label htmlFor={id} style={{ display: "block", fontFamily: "'Gilroy', -apple-system, Roboto, Helvetica, sans-serif", fontSize: 12, fontWeight: 400, color: "#737070", marginBottom: 4 }}>
            {title}
          </label>
        )}
        <div style={{ position: "relative" }}>
          <input
            id={id} name={name} type="text"
            readOnly={isReadOnly} disabled={isDisabled}
            placeholder={placeholder}
            value={typedValue}
            onChange={handleInputChange}
            style={inputStyle}
            onFocus={(e) => { e.currentTarget.style.borderColor = "#079f9f"; }}
            onBlur={(e) => { e.currentTarget.style.borderColor = isInputValid ? "#E0E0E0" : "#ef4444"; }}
          />
          <img
            src="/assets/Calendar.svg"
            alt="Calendar"
            onClick={() => !isDisabled && !isReadOnly && setIsOpen(!isOpen)}
            style={{
              position: "absolute", right: "16px", top: "50%", transform: "translateY(-50%)",
              width: "24px", height: "24px", cursor: isDisabled || isReadOnly ? "not-allowed" : "pointer",
            }}
          />
        </div>
        {!isInputValid && errorText && (
          <p style={{ color: "#991b1b", fontFamily: "'Gilroy', -apple-system, Roboto, Helvetica, sans-serif", fontWeight: 500, fontSize: 12, marginTop: 8 }}>{errorText}</p>
        )}
        {isOpen && renderCalendar()}
      </div>
    </div>
  );
}
